import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { LoadingService } from '../../services/loading.service';
import { catchError, finalize, Observable, tap, throwError } from 'rxjs';
import { inject } from '@angular/core';
import { FlagService } from '../../shared/services/flag.service';
import { FlagType } from '../../shared/utils/enums';
import {AuthService} from "../../services/auth.service";
import { environment } from '../../../environments/environment';

export const functionalInterceptor: HttpInterceptorFn = (req, next) => {

  const flag = inject(FlagService); // Instantiate the loading service
  const auth = inject(AuthService); // Instantiate the loading service

  // Only our own backend gets the JWT — external APIs (scanfacture SIRET lookup,
  // geo.api.gouv.fr, nominatim...) must never receive the session token, and their
  // 401s must not be mistaken for an expired Collary session.
  const isApiCall = req.url.startsWith(environment.api.BASE_URL);

  const authReq = isApiCall
    ? req.clone({
        setHeaders: {
          Authorization: `Bearer ${auth.token}`,
        },
      })
    : req;

  // Public auth endpoints: a 401 here means bad credentials, not an expired session.
  const isAuthCall =
    req.url.includes('/auth/sign_in') ||
    req.url.includes('/auth/sign_up') ||
    req.url.includes('/auth/onboard') ||
    req.url.includes('/auth/onboarding-status') ||
    req.url.includes('/auth/check_verification_code') ||
    req.url.includes('/password/forgot');

  // Login second step & collaborator invitations: their screens display every error themselves
  // (remaining attempts, expired link...), so no generic toast on top.
  const selfHandledCall =
    req.url.includes('/auth/2fa/') ||
    req.url.includes('/auth/invitations') ||
    req.url.includes('/agencies/users') ||
    req.url.includes('/agencies/audit');

  return next(authReq).pipe(
    tap((event) => {}),
    catchError((err: any) => {
      if (err instanceof HttpErrorResponse) {
        // Handle HTTP errors
        const sessionEnded = err.status === 401 || (err.status === 403 && auth.isTokenExpired());
        if (isApiCall && sessionEnded && !selfHandledCall) {
          if (isAuthCall) {
            flag.displayFlag('Non autorisé !', FlagType.error);
          } else if (auth.user) {
            // Expired/invalid session (or account disabled by an admin): clear it and go back to login.
            flag.displayFlag('Session expirée, veuillez vous reconnecter.', FlagType.error);
            auth.disconnect();
          }
        } else if (isApiCall && sessionEnded && selfHandledCall && auth.user
                   && !req.url.includes('/auth/')) {
          // Protected self-handled screens (collaborators, audit): a 401 still ends the session.
          flag.displayFlag('Session expirée, veuillez vous reconnecter.', FlagType.error);
          auth.disconnect();
        } else if (isApiCall && selfHandledCall) {
          // Business errors are displayed by the calling component.
        } else if (isApiCall && err.status === 403) {
          // RBAC refusal: the session is valid, the role just lacks the permission.
          flag.displayFlag(
            err.error?.message || "Accès refusé : vous n'avez pas les droits nécessaires pour cette action.",
            FlagType.error
          );
        } else {
          if (err.status !== 200) {
            // Handle other HTTP error codes
            flag.displayFlag('Une erreur est survenue !', FlagType.error);
          }
        }
      } else {
        // Handle non-HTTP errors

        flag.displayFlag('Une erreur est survenue !', FlagType.error);
      }

      // Re-throw the error to propagate it further
      return throwError(() => err);
    })
  );
};

// Loading Spinner Interceptor
export const loadingSpinnerInterceptorFunctional: HttpInterceptorFn = (
  req,
  next
) => {
  const loadingService = inject(LoadingService); // Instantiate the loading service

  // loadingService.toggleLoading(true); // Show loading spinner UI element
  setTimeout(() => {
    loadingService.toggleLoading(true);
  }, 0);
  return next(req).pipe(
    finalize(() => {
      // Hide loading spinner UI element
      setTimeout(() => {
        loadingService.toggleLoading(false);
      }, 700);
    })
  );
};
